
import React from 'react';
import { siteConfig } from '../config/site';
import { Shield, Zap, ChevronDown, Home } from 'lucide-react';
import { useLanguage } from '../App';

const BrandPage: React.FC = () => {
  const { t } = useLanguage();
  return (
    <div className="bg-primary overflow-hidden">
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={siteConfig.images.heroFallback} alt="Brand" className="w-full h-full object-cover opacity-10 grayscale scale-110 animate-pulse" />
          <div className="absolute inset-0 bg-gradient-to-t from-primary to-transparent"></div>
        </div>
        <div className="relative z-10 text-center px-6">
          <h1 className="text-5xl sm:text-7xl font-serif font-bold uppercase accent-text-gradient tracking-widest mb-4 animate-fade-up">{t.brandStoryTitle}</h1>
          <p className="text-accent/60 text-sm tracking-[0.5em] font-bold uppercase animate-fade-up delay-1">{t.brandStoryTagline}</p>
        </div>
      </section>

      <section className="py-24 px-6 max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-center">
        <div className="space-y-8 reveal reveal-left">
          <h2 className="text-4xl font-serif font-bold text-accent uppercase tracking-tighter">{t.brandTouch}</h2>
          <p className="text-white/70 leading-relaxed text-lg">{t.brandDesc1}</p>
          <p className="text-white/70 leading-relaxed font-light">{t.brandDesc2}</p>
          
          <div className="pt-6">
            <div className="relative p-1 bg-accent/10 border border-accent/20 rounded-sm overflow-hidden">
               <div className="aspect-video w-full">
                 <iframe src={siteConfig.videos.homeFeatured} className="w-full h-full border-0" allow="autoplay; fullscreen" loading="lazy"></iframe>
               </div>
               <div className="p-3 bg-black/40 text-[10px] text-accent font-black uppercase tracking-[0.3em] flex justify-between items-center">
                 <span>{t.officialFilm}</span>
                 <span className="opacity-40">MXXIV</span>
               </div>
            </div>
          </div>

          <div className="flex items-center gap-4 border-l-2 border-accent pl-6 py-2">
            <div>
              <p className="font-serif italic text-white/90">"{t.brandQuote}"</p>
              <p className="text-accent text-xs font-bold uppercase mt-2 tracking-widest">— Santiago, Santi Bladez</p>
            </div>
          </div>
        </div>
        <div className="relative group reveal reveal-right">
          <div className="absolute -inset-4 border border-accent/20 translate-x-4 translate-y-4 group-hover:translate-x-2 group-hover:translate-y-2 transition-transform duration-500"></div>
          <img src={siteConfig.images.brandStory} alt="Santiago" className="relative z-10 w-full h-[600px] object-cover grayscale hover:grayscale-0 transition-all duration-700 shadow-2xl" />
        </div>
      </section>

      <section className="py-24 px-6 bg-secondary/30 border-y border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 reveal reveal-up">
            <h2 className="text-3xl font-serif font-bold uppercase tracking-widest mb-2">{t.myEvolution}</h2>
            <div className="w-20 h-1 bg-accent mx-auto"></div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {t.milestones.map((m: any, idx: number) => (
              <div key={idx} className={`reveal reveal-up bg-primary/50 p-8 border border-white/5 hover:border-accent transition-all group backdrop-blur-sm`}>
                <span className="text-4xl font-serif font-bold text-accent/20 group-hover:text-accent transition-colors block mb-4">{m.year}</span>
                <h4 className="text-xl font-serif font-bold mb-3 uppercase tracking-tight">{m.title}</h4>
                <p className="text-white/50 text-sm leading-relaxed">{m.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-6 bg-secondary/50 border-b border-accent/10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 reveal reveal-up">
            <h2 className="text-3xl font-serif font-bold uppercase tracking-widest mb-2">{t.coreStandards}</h2>
            <div className="w-20 h-1 bg-accent mx-auto"></div>
          </div>
          <div className="grid md:grid-cols-3 gap-12">
            {t.coreValues.map((v: any, i: number) => (
              <div key={i} className={`text-center space-y-4 reveal reveal-up`}>
                <div className="inline-flex p-4 rounded-full bg-accent/10 text-accent mb-2">
                  {i === 0 ? <Home size={32} /> : i === 1 ? <Shield size={32} /> : <Zap size={32} />}
                </div>
                <h4 className="text-xl font-serif font-bold uppercase tracking-wide">{v.title}</h4>
                <p className="text-white/50 text-sm leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="faq" className="py-24 px-6 max-w-3xl mx-auto">
        <div className="text-center mb-16 reveal reveal-up">
          <h2 className="text-3xl font-serif font-bold uppercase tracking-widest mb-2">{t.faqTitle}</h2>
          <div className="w-20 h-1 bg-accent mx-auto"></div>
        </div>
        <div className="space-y-4 reveal reveal-up">
          {t.faq.map((item: any, idx: number) => (
            <details key={idx} className="group bg-secondary/30 border border-white/5 open:border-accent/30 transition-all shadow-lg">
              <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                <span className="font-bold uppercase text-xs tracking-widest text-white/80 group-open:text-accent transition-colors">{item.q}</span>
                <ChevronDown className="text-accent group-open:rotate-180 transition-transform" />
              </summary>
              <div className="px-6 pb-6 text-sm text-white/50 leading-relaxed border-t border-white/5 pt-4">
                {item.a}
              </div>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
};

export default BrandPage;
